/**
 * Wrapper classes and custom properties for Info Box.
 *
 * Shared by edit.jsx and save.jsx so the editor preview and the saved markup
 * cannot drift — a static-save block that computes its wrapper twice is the
 * classic source of "block contains unexpected content" validation errors.
 */

import { getBlockGapValue, resolvePresetValue } from '../../utils/helpers';

/**
 * @param {Object} attributes Block attributes.
 * @return {string} Wrapper class list.
 */
export function getWrapperClasses(attributes) {
  const { layout, contentAlign, iconShape, hoverEffect } = attributes;

  return [
    'brandy-info-box',
    `is-layout-${layout}`,
    `is-aligned-${contentAlign}`,
    iconShape && iconShape !== 'none' ? `has-icon-${iconShape}` : '',
    hoverEffect && hoverEffect !== 'none' ? `has-hover-${hoverEffect}` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Core's Block spacing value, resolved to something a `gap` can use.
 *
 * Core only ever writes blockGap through the layout support, and this block
 * deliberately has none — its flex row is its own, and core's layout CSS would
 * fight the stacked/inline direction. Declaring the support without
 * serializing it left the Dimensions control writing an attribute nothing
 * read, which is the bug this replaces. `__experimentalSkipSerialization` in
 * block.json states that this file owns the value, so nothing double-applies
 * if the block ever gains layout support later.
 *
 * Reading the standard attribute rather than a private one is what keeps the
 * spacing presets, the reset, and Global Styles all working normally.
 *
 * The axis pick and the length check live in getBlockGapValue(), shared with
 * the blocks that serialize blockGap from PHP.
 */

/**
 * The icon's size and colours are custom properties rather than inline styles
 * on the <i> itself, because the shaped backdrop (circle/square) is drawn by
 * the wrapper's stylesheet and needs the same numbers to size its padding.
 *
 * @param {Object} attributes Block attributes.
 * @return {Object} React style object.
 */
export function getWrapperStyles(attributes) {
  const { iconSize, iconColor, iconBackgroundColor, style } = attributes;

  const styles = {
    '--brandy-info-box-icon-size': `${iconSize}px`,
  };

  const gap = getBlockGapValue(style?.spacing?.blockGap);

  // Left unset when empty so the stylesheet's fallback chain decides, rather
  // than pinning the global gap into every wrapper.
  if (gap) {
    styles['--brandy-info-box-gap'] = gap;
  }

  if (iconColor) {
    styles['--brandy-info-box-icon-color'] = resolvePresetValue(iconColor);
  }

  if (iconBackgroundColor) {
    styles['--brandy-info-box-icon-bg'] = resolvePresetValue(iconBackgroundColor);
  }

  return styles;
}
